import { PileId } from './logic-types';
import { Board } from './board';
import { Pile } from './card/pile';
import { Card } from './card/card';

export type PossibleMove = {
  originPileId: PileId;
  cardIndex: number;
};

export type PossibleMoves = {
  tableauMoves: PossibleMove[];
  deckMoves: PossibleMove[];
  topWasteMoves: PossibleMove[];
  buriedWasteMoves: PossibleMove[];
};

export class PossibleMovesFinder {
  public static findPossibleMoves(board: Board): PossibleMoves {
    return {
      tableauMoves: this.getTableauMoves(board),
      deckMoves: this.getDeckMoves(board),
      topWasteMoves: this.getTopWasteCardMoves(board),
      buriedWasteMoves: this.getBuriedWasteCardMoves(board),
    };
  }

  private static getTableauMoves(board: Board): PossibleMove[] {
    const possibleMoves: PossibleMove[] = [];

    board.tableaus.forEach((originPile) => {
      const validCards = originPile.allCards.filter(
        (card) => card.cardId !== undefined && card.isFaceUp,
      );
      validCards.forEach((card) => {
        const foundationMoves: PossibleMove[] = board.foundations
          .filter((targetPile) => card.canMove(originPile, targetPile))
          .map((_targetPile) => ({
            originPileId: originPile.id,
            cardIndex: card.index,
          }));
        const tableauMoves: PossibleMove[] = board.tableaus
          .filter(
            (targetPile) =>
              card.canMove(originPile, targetPile) &&
              this.isMoveInsideTableauUseful(board, targetPile, card),
          )
          .map((_targetPile) => ({
            originPileId: originPile.id,
            cardIndex: card.index,
          }));

        possibleMoves.push(...foundationMoves, ...tableauMoves);
      });
    });

    return possibleMoves;
  }

  private static isMoveInsideTableauUseful(board: Board, targetPile: Pile, card: Card): boolean {
    const parentCard = board.parentCard(card);
    const topTargetPileCard = targetPile.lastCard;

    if (this.isMovingOnTheSameValue(parentCard, topTargetPileCard)) {
      return false;
    }

    if (card.cardId?.isKing && this.isMovingBetweenEmptyPiles(parentCard, topTargetPileCard)) {
      return false;
    }

    return true;
  }

  private static isMovingOnTheSameValue(
    parentCard: Card | undefined,
    topTargetPileCard: Card | undefined,
  ): boolean {
    if (parentCard === undefined || topTargetPileCard === undefined) {
      return false;
    }

    if (!parentCard.isFaceUp) {
      return false;
    }

    if (parentCard.cardId === undefined || topTargetPileCard.cardId === undefined) {
      return false;
    }

    return parentCard.cardId.cardValue == topTargetPileCard.cardId.cardValue;
  }

  private static isMovingBetweenEmptyPiles(
    parentCard: Card | undefined,
    topTargetPileCard: Card | undefined,
  ): boolean {
    return parentCard === undefined && topTargetPileCard === undefined;
  }

  private static getDeckMoves(board: Board): PossibleMove[] {
    const possibleMoves: PossibleMove[] = [];

    const allDeckCards = board.deck.allCards;

    for (let i = allDeckCards.length - 1; i >= 0; i--) {
      const card = allDeckCards[i];

      const foundationMoves: PossibleMove[] = board.foundations
        .filter((targetPile) => card.canMove(board.deck, targetPile))
        .map((_targetPile) => ({
          originPileId: PileId.Deck,
          cardIndex: card.index,
        }));
      const tableauMoves: PossibleMove[] = board.tableaus
        .filter((targetPile) => card.canMove(board.deck, targetPile))
        .map((_targetPile) => ({
          originPileId: PileId.Deck,
          cardIndex: card.index,
        }));
      possibleMoves.push(...foundationMoves, ...tableauMoves);
    }

    return possibleMoves;
  }

  private static getTopWasteCardMoves(board: Board): PossibleMove[] {
    const possibleMoves: PossibleMove[] = [];

    if (!board.waste.hasCards) {
      return possibleMoves;
    }

    const topWasteCard = board.waste.lastCard;
    if (topWasteCard === undefined) {
      return possibleMoves;
    }

    const foundationMoves: PossibleMove[] = board.foundations
      .filter((targetPile) => topWasteCard.canMove(board.deck, targetPile))
      .map((_targetPile) => ({
        originPileId: PileId.Waste,
        cardIndex: topWasteCard.index,
      }));
    const tableauMoves: PossibleMove[] = board.tableaus
      .filter((targetPile) => topWasteCard.canMove(board.deck, targetPile))
      .map((_targetPile) => ({
        originPileId: PileId.Waste,
        cardIndex: topWasteCard.index,
      }));
    possibleMoves.push(...foundationMoves, ...tableauMoves);

    return possibleMoves;
  }

  private static getBuriedWasteCardMoves(board: Board): PossibleMove[] {
    const possibleMoves: PossibleMove[] = [];

    if (!board.waste.hasCards) {
      return possibleMoves;
    }

    const wasteCards = board.waste.allCards;
    // Don't include the last waste card
    for (let i = 0; i < wasteCards.length - 2; ++i) {
      const card = wasteCards[i];

      const foundationMoves: PossibleMove[] = board.foundations
        .filter((targetPile) => card.canMove(board.deck, targetPile))
        .map((_targetPile) => ({
          originPileId: PileId.Waste,
          cardIndex: card.index,
        }));
      const tableauMoves: PossibleMove[] = board.tableaus
        .filter((targetPile) => card.canMove(board.deck, targetPile))
        .map((_targetPile) => ({
          originPileId: PileId.Waste,
          cardIndex: card.index,
        }));
      possibleMoves.push(...foundationMoves, ...tableauMoves);
    }

    return possibleMoves;
  }
}
